import { between } from 'lib/util';
import { getLineSegmentIntersection, getLineSegmentRectIntersections, lineSegmentFullyWithinRect, pointDistance } from 'lib/geom';
import { DOG_LEG_AVOID_CARD_MARGIN, DOG_LEG_BEND_CURVATURE } from 'lib/constants';

class LinksUI {
  constructor() {
    this.fromPoints = {};
    this.toPoints = {};
    this.skewDeltas = {};
  }

  setCanvas(canvas) {
    this.canvas = canvas;
  }

  getDashArray(type, width) {
    if (type == 'dotted') {
      if (width == 'xs') {
        return '1.5px 1.5px';
      } else if (width == 's') {
        return '3px 3px';
      } else if (width == 'm') {
        return '4.5px 4.5px';
      } else if (width == 'l') {
        return '6px 6px';
      } else if (width == 'xl') {
        return '8px 8px';
      } else {
        return '3px 3px';
      }
    } else if (type == 'dashed') {
      if (width == 'xs') {
        return '4.5px 1.5px';
      } else if (width == 's') {
        return '9px 3px';
      } else if (width == 'm') {
        return '13.5px 4.5px';
      } else if (width == 'l') {
        return '18px 6px';
      } else if (width == 'xl') {
        return '24px 8px';
      } else {
        return '9px 3px';
      }
    } else if (type == 'solid') {
      return 0;
    }
  }
  getArcPath({ fromEdgePoint, radius, sweepFlag, largeArcFlag, toEdgePoint }) {
    return [
      'M', fromEdgePoint.x, fromEdgePoint.y,
      'A', radius, radius, 0, largeArcFlag, sweepFlag, toEdgePoint.x, toEdgePoint.y,
    ].join(' ');
  }

  getOuterBox() {
    return {
      topLeft: {
        x: circleCenter.x - radius - 10,
        y: circleCenter.y - radius - 10
      },
      width: 2 * radius + 20,
      height: 2 * radius + 20,
    };
  }

  removeEndSegments(paths, fromRect, toRect, isParentToChild, isChildToParent) {
    // don't clip if we're pointing to a collapsed child dot
    const clipFrom = !fromRect._isAncestor;
    const clipTo = !toRect._isAncestor;
    paths.forEach(path => {
      path._clip = 0;
      if (path.length > 2) {
        for (let i = 0; i < path.length - 1; i++) {
          if (clipFrom && !isParentToChild && lineSegmentFullyWithinRect(path[i], path[i + 1], fromRect)) {
            path._clip++;
            path.splice(i, 1);
          } else if (clipTo && !isChildToParent && lineSegmentFullyWithinRect(path[i], path[i + 1], toRect)) {
            path._clip++;
            path.splice(i + 1, 1);
          }
        }
      }

      if (!isParentToChild && clipFrom) {
        for (let i = 0; i < 1; i++) {
          const fromRectIntersections = getLineSegmentRectIntersections(path[i], path[i + 1], fromRect);
          if (fromRectIntersections && fromRectIntersections.length) path[i] = fromRectIntersections[0];
        }
      }

      if (!isChildToParent && clipTo) {
        for (let i = path.length - 2; i < path.length - 1; i++) {
          const toRectIntersections = getLineSegmentRectIntersections(path[i], path[i + 1], toRect);
          if (toRectIntersections && toRectIntersections.length) path[i + 1] = toRectIntersections[0];
        }
      }
    });
  }

  squishPaths(paths) {
    paths.forEach(path => {
      if (path.length > 2) {
        for (let i = 0; i < path.length - 2; i++) {
          if (path[i].x == path[i+1].x && path[i].x == path[i+2].x) {
            if ((path[i].y < path[i+1].y && path[i+1].y < path[1+2].y) || (path[i].y > path[i+1].y && path[i+1].y > path[1+2].y)) {
              path.splice(i + 1, 1);
              i--;
            } else {
              path.useless = true;
            }
          } else if (path[i].y == path[i+1].y && path[i].y == path[i+2].y) {
            if ((path[i].x < path[i+1].x && path[i+1].x < path[1+2].x) || (path[i].x > path[i+1].x && path[i+1].x > path[1+2].x)) {
              path.splice(i + 1, 1);
              i--;
            } else {
              path.useless = true;
            }
          }
        }
      }
    });
  }

  eliminateSelfIntersections(paths) {
    paths.forEach(path => {
      if (path.length > 2) {
        for (let i = 0; i < path.length - 1; i++) {
          for (let j = 0; j < path.length - 1; j++) {
            if (i == j) continue;
            if (Math.abs(i - j) <= 1) continue;
            if (getLineSegmentIntersection(path[i], path[i + 1], path[j], path[j + 1])) {
              path.useless = true;
            }
          }
        }
      }
    });
  }

  eliminateDiagonals(paths) {
    paths.forEach(path => {
      if (path.length > 1) {
        for (let i = 0; i < path.length - 1; i++) {
          if (path[i].x !== path[i + 1].x && path[i].y !== path[i + 1].y) {
            path.useless = true;
          }
        }
      }
    });
  }

  _getNewTarget({ currentPoint, targets }) {
    if (!targets.length) return false;
    if ( currentPoint.x == targets[0].x && currentPoint.y === targets[0].y ) {
      targets.shift();
      return targets[0];
    } else {
      return targets[0];
    }
  };

  findPaths({ thirdPoint, fromPoint, toPoint, fromRect, toRect, isParentToChild, isChildToParent, linkId }) {
    const paths = [];

    const sanitizeRect = (r) => {
      // sync left/x and top/y if they're out of sync
      if ('left' in r) r.x = r.left;
      if ('x' in r) r.left = r.x;
      if ('top' in r) r.y = r.top;
      if ('y' in r) r.top = r.y;
      if ('width' in r) r.right = r.x + r.width;
      if ('right' in r) r.width = r.right - r.left;
      if ('height' in r) r.bottom = r.y + r.height;
      if ('bottom' in r) r.height = r.bottom - r.top;
    };

    sanitizeRect(thirdPoint);
    sanitizeRect(fromPoint);
    sanitizeRect(toPoint);
    sanitizeRect(fromRect);
    sanitizeRect(toRect);

    let guard = 0;

    const choosePath = ({ currentPoint, path, targets, debug }) => {
      const targetPoint = this._getNewTarget({ currentPoint, targets });
      guard++;

      if (!targetPoint || guard > 1000) {
        path.debug = debug;
        paths.push(path);
        return;
      }
      const margin = DOG_LEG_AVOID_CARD_MARGIN;

      const maybeGoAround = (originalDestination) => {
        if (path.length >= 1) {
          const lastPoint = path[path.length - 1];
          const fromRectIntersections = getLineSegmentRectIntersections(lastPoint, originalDestination, fromRect);
          const toRectIntersections = getLineSegmentRectIntersections(lastPoint, originalDestination, toRect);
          const segmentOrientation = lastPoint.x === originalDestination.x ? 'vertical' : 'horizontal';

          if (fromRectIntersections.length > 1 && toRectIntersections.length > 1) {
            if (segmentOrientation === 'horizontal') {
              const nearestEdge = (currentPoint.y - Math.min(toRect.top, fromRect.top)) < (currentPoint.y - Math.max(toRect.bottom, fromRect.bottom))
                ? 'top' : 'bottom';
              if (nearestEdge === 'top') {
                return [
                  { x:  currentPoint.x, y: Math.min(fromRect.top, toRect.top) - margin },
                  { x:  targetPoint.x, y: Math.min(fromRect.top, toRect.top) - margin }
                ];
              } else {
                return [
                  { x:  currentPoint.x, y: Math.max(fromRect.bottom, toRect.bottom) + margin },
                  { x:  targetPoint.x, y: Math.max(fromRect.bottom, toRect.bottom) + margin }
                ];
              }
            } else {
              const nearestEdge = (currentPoint.x - Math.min(toRect.left, fromRect.left)) < (currentPoint.x - Math.max(toRect.right, fromRect.right))
                ? 'left' : 'right';
              if (nearestEdge === 'left') {
                return [
                  { y:  currentPoint.y, x: Math.min(fromRect.left, toRect.left) - margin },
                  { y:  targetPoint.y, x: Math.min(fromRect.left, toRect.left) - margin }
                ];
              } else {
                return [
                  { y:  currentPoint.y, x: Math.max(fromRect.right, toRect.right) + margin },
                  { y:  targetPoint.y, x: Math.max(fromRect.right, toRect.right) + margin }
                ];
              }
            }
          } else if (fromRectIntersections.length > 1) {
            if (segmentOrientation === 'horizontal') {
              const nearestEdge = (currentPoint.y - fromRect.top) < (currentPoint.y - fromRect.bottom) ? 'top' : 'bottom';
              if (nearestEdge === 'top') {
                return [
                  { x:  currentPoint.x, y: fromRect.top - margin },
                  { x:  targetPoint.x, y: fromRect.top - margin }
                ];
              } else {
                return [
                  { x:  currentPoint.x, y: fromRect.bottom + margin },
                  { x:  targetPoint.x, y: fromRect.bottom + margin }
                ];
              }
            } else {
              const nearestEdge = (currentPoint.x - fromRect.left) < (currentPoint.x - fromRect.right) ? 'left' : 'right';
              if (nearestEdge === 'left') {
                return [
                  { y:  currentPoint.y, x: fromRect.left - margin },
                  { y:  targetPoint.y, x: fromRect.left - margin }
                ];
              } else {
                return [
                  { y:  currentPoint.y, x: fromRect.right + margin },
                  { y:  targetPoint.y, x: fromRect.right + margin }
                ];
              }
            }
          } else if (toRectIntersections.length > 1) {
            if (segmentOrientation === 'horizontal') {
              const nearestEdge = (currentPoint.y - toRect.top) < (currentPoint.y - toRect.bottom) ? 'top' : 'bottom';
              if (nearestEdge === 'top') {
                return [
                  { x:  currentPoint.x, y: toRect.top - margin },
                  { x:  targetPoint.x, y: toRect.top - margin }
                ];
              } else {
                return [
                  { x:  currentPoint.x, y: toRect.bottom + margin },
                  { x:  targetPoint.x, y: toRect.bottom + margin }
                ];
              }
            } else {
              const nearestEdge = (currentPoint.x - toRect.left) < (currentPoint.x - toRect.right) ? 'left' : 'right';
              if (nearestEdge === 'left') {
                return [
                  { y:  currentPoint.y, x: toRect.left - margin },
                  { y:  targetPoint.y, x: toRect.left - margin }
                ];
              } else {
                return [
                  { y:  currentPoint.y, x: toRect.right + margin },
                  { y:  targetPoint.y, x: toRect.right + margin }
                ];
              }
            }
          }
        }
        return [originalDestination];
      };

      // if we're in line with our target, we have to go to the target next
      if (currentPoint.x === targetPoint.x || currentPoint.y === targetPoint.y) {
        const newDestinations = maybeGoAround({ x: targetPoint.x, y: targetPoint.y });
        choosePath({
          currentPoint: newDestinations[newDestinations.length - 1],
          path: path.slice().concat(newDestinations),
          targets: targets.slice(),
          debug: debug + "d"
        });
      } else {
      // otherwise, choose both options
        const newDestinationsA = maybeGoAround({ x: currentPoint.x, y: targetPoint.y });
        choosePath({
          currentPoint: newDestinationsA[newDestinationsA.length - 1],
          path: path.slice().concat(newDestinationsA),
          targets: targets.slice(),
          debug: debug + "b",
        });

        const newDestinationsB = maybeGoAround({ x: targetPoint.x, y: currentPoint.y });
        choosePath({
          currentPoint: newDestinationsB[newDestinationsB.length - 1],
          path: path.slice().concat(newDestinationsB),
          targets: targets.slice(),
          debug: debug + "c",
        });
      }

    };

    const margin = DOG_LEG_AVOID_CARD_MARGIN;
    if (isParentToChild) {
      choosePath({
        currentPoint: { x: fromPoint.x - margin, y: fromPoint.y },
        path: [{ x: fromPoint.x, y: fromPoint.y }, { x: fromPoint.x - margin, y: fromPoint.y }],
        targets: [thirdPoint, toPoint],
      });
    } else if (isChildToParent) {
      choosePath({
        currentPoint: fromPoint,
        path: [{ x: fromPoint.x, y: fromPoint.y }],
        targets: [thirdPoint, { x: toPoint.x - margin, y: toPoint.y }, toPoint],
      });
    } else {
      choosePath({
        currentPoint: fromPoint,
        path: [{ x: fromPoint.x, y: fromPoint.y }],
        targets: [thirdPoint, toPoint],
        debug: "a"
      });
    }

    for (const p of paths) {
      p._orig = JSON.parse(JSON.stringify(p));
    }


    this.squishPaths(paths);
    this.eliminateSelfIntersections(paths);
    this.eliminateDiagonals(paths);
    this.removeEndSegments(paths, fromRect, toRect, isParentToChild, isChildToParent);
    return paths;
  }

  getTurnPath({ from, to, curvature }) {
    const d = to === 'above' || to === 'left' ? -curvature : curvature;

    switch (from) {
      case 'left': return `-${curvature} 0 -${curvature} ${d}`;
      case 'right': return `${curvature} 0 ${curvature} ${d}`;
      case 'above': return `0 -${curvature} ${d} -${curvature}`;
      case 'bellow': return `0 ${curvature} ${d} ${curvature}`;
      default: return '0 0 0 0';
    }
  }

  getTurnDirection({ current: { x, y }, prev, next }) {
    return {
      from: x === prev.x ? y < prev.y ? 'above' : 'bellow' : x < prev.x ? 'left' : 'right',
      to: x === next.x ? y > next.y ? 'above' : 'bellow' : x > next.x ? 'left' : 'right',
    };
  }

  getPath(current, index, list) {
    const { x, y } = current;
    const left = Math.round(x);
    const top = Math.round(y);

    // We don’t want any curves on the start and end points
    if (index === 0 || index === list.length - 1) {
      return `${left} ${top}`;
    }

    const prev = list[index - 1];
    const next = list[index + 1];

    const curvature = Math.min(pointDistance(prev,current)/2, pointDistance(current,next)/2, DOG_LEG_BEND_CURVATURE);

    const { from, to } = this.getTurnDirection({ current, prev, next });

    const offsetX = from === 'left' ? curvature : from === 'right' ? -curvature : 0;
    const offsetY = from === 'above' ? curvature : from === 'bellow' ? -curvature : 0;


    return `${left + offsetX} ${top + offsetY} s ${this.getTurnPath({ from, to, curvature })}`;
  }

  getDogLegPath({ thirdPoint, fromCenterPoint, toCenterPoint, fromPoint, fromRect, toPoint, toRect, fromNodeId, toNodeId, linkId, isParentToChild, isChildToParent }) {

    // const naivePath = `M ${fromPoint.x} ${fromPoint.y} L ${thirdPoint.x} ${thirdPoint.y} L ${toPoint.x} ${toPoint.y}`;
    // return { pathString: naivePath, path: [ { x: fromPoint.x, y: fromPoint.y }, { x: thirdPoint.x, y: thirdPoint.y }, { x: toPoint.x, y: toPoint.y} ] };

    const parallelScore = (path) => {
      // boost if first and last segments are parallel, and moreso if they're horizontal
      if (!path.length) return 0;

      const firstSegmentOrientation = path[0].x == path[1].x ? 'v' : 'h';

      const lastSegmentOrientation = path.slice(-1)[0].x == path.slice(-2)[0].x ? 'v' : 'h';

      return (firstSegmentOrientation == lastSegmentOrientation)
             * (firstSegmentOrientation == 'h' ? -2.1 : -2);
    };

    const stubScore = (path) => {
      // penalize if the first and/or last segments are too short
      return (pointDistance(path[0], path[1]) < 10) * 1 + (pointDistance(path.slice(-1)[0], path.slice(-2)[0]) < 10) * 1;
    };

    const clipScore = (path) => {
      return path._clip * 1.5;
    };

    const paths = this.findPaths({
      linkId,
      fromPoint: fromPoint,
      toPoint: toPoint,
      thirdPoint: thirdPoint,
      fromRect: fromRect,
      toRect: toRect,
      isParentToChild,
      isChildToParent,
    })
    // .filter(({ useless }) => !useless)
    .sort((a, b) => {
      const penalty = 100000000;

      return (
        (a.length + Boolean(a.useless) * penalty + parallelScore(a) + stubScore(a) + clipScore(a)) -
        (b.length + Boolean(b.useless) * penalty + parallelScore(b) + stubScore(b) + clipScore(b))
      );
    });


    //const shortest = paths[0]._orig;
    const shortest = paths[0];
    const link = store.getLink(linkId);

    link._path = shortest;
    if (link.fromNode()) {
      this.nudgeSegments({ linkId: link.id, path: link._path, node: link.fromNode(), reverse: false });
    }
    if (link.toNode()) {
      this.nudgeSegments({ linkId: link.id, path: link._path, node: link.toNode(), reverse: true });
    }

    window.lastPath = shortest;

    return { pathString: 'M' + link._path.map(this.getPath.bind(this)).join(' L '), path: link._path };
  }

  nudgeSegments({ linkId, path, node, reverse }) {

    const threshold = 5;
    const offsetIncrement = 1;
    let collisionLink;
    let offset = 1;

    const localPath = reverse ? path.slice().reverse() : path.slice();

    do {
      collisionLink = null;
      const nodeLinks = node.links();
      for (const l of nodeLinks) {

        if (linkId == l.id) continue;
        if (!l._path) continue;

        const remotePath = reverse ? l._path.slice().reverse() : l._path.slice();

        const testPoints = [
          [ localPath[0], remotePath[0] ],
          [ localPath[0], remotePath.slice(-1)[0] ]
        ];

        for (const [p1, p2] of testPoints) {
          if (pointDistance(p1, p2) < threshold && l.id < linkId) {
            collisionLink = l;
          }
        }
      }

      if (collisionLink) {

        const remotePath = collisionLink._path;

        let offsetDir = 1;

        if (localPath[0].x == localPath[1].x) {
          if (localPath[2] && remotePath[2] && localPath[2].x < remotePath[2].x) {
            offsetDir = -1;
          }

          //if we have room to nudge
          if (!(localPath[0].x + offsetDir * offset > node.absolutePosition.x + node._width || localPath[0].x + offsetDir * offset < node.absolutePosition.x)) {
            localPath[0].x += offsetDir * offset;
            localPath[1].x += offsetDir * offset;
          } else {
            break;
          }
        } else {
          if (localPath[2] && remotePath[2] && localPath[2].y < remotePath[2].y) {
            offsetDir = -1;
          }
          if (!(localPath[0].y + offsetDir * offset > node.absolutePosition.y + node._height || localPath[0].y + offsetDir * offset < node.absolutePosition.y)) {
            localPath[0].y += offsetDir * offset;
            localPath[1].y += offsetDir * offset;
          } else {
            break;
          }
        }
        offset++;
      }
    } while (collisionLink);

  }

}

const linksUI = new LinksUI();

export { linksUI, LinksUI };



// WEBPACK FOOTER //
// ./starling/src/lib/links-ui.js